import { Injectable } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { Observable, Subject, ReplaySubject, of, concat, throwError, defer } from 'rxjs';
import { takeUntil, tap, map, concatMap, toArray } from 'rxjs/operators';
import { uFormat, RequestRoot, PassportNumber, PassportsStore, RequestEvent, UTCDate } from 'types';
import { ValidationResult, ValidationSeverity } from 'validation';
import { RequestDataAccessService } from 'data-access';
import { Features } from '../model/features';
import { ValidationBuilder } from '../validation/validation.builder';
import { TableItem } from '../table/table';
import { AppContext, WizardContext } from './context';

export class CommandSideEffects {
  databaseModified: boolean;
  validationTriggered: ValidationResult;
  timestamp: ReplaySubject<Date>;
}

export abstract class CommandPage {
  private loadingRef = 0;

  isLoading(): boolean {
    return this.loadingRef > 0;
  }

  incrementLoadingRef() {
    this.loadingRef++;
  }

  decrementLoadingRef(n?: number) {
    n = n || 1;
    if (this.loadingRef >= n) {
      this.loadingRef -= n;
    } else {
      console.error('loadingRef');
    }
  }

  private printFormKey: string | null = null;
  private printFormModel: RequestRoot;
  private printForm$: Subject<RequestRoot>;

  getPrintFormKey(): string | null {
    return this.printFormKey;
  }

  getPrintFormModel(): RequestRoot {
    return this.printFormModel;
  }

  openPrintForm(printFormKey: string, printFormModel: RequestRoot): Observable<RequestRoot> {
    this.printFormKey = printFormKey;
    this.printFormModel = printFormModel;
    this.printForm$ = new Subject<RequestRoot>();
    return this.printForm$;
  }

  onPrintFormClosed() {
    this.printFormKey = null;
    this.printForm$.next(this.printFormModel);
    this.printForm$.complete();
  }

  private commandProgress = new Subject<string>();
  private commandError = new Subject<string>();
  private commandWarning = new Subject<string>();

  getCommandProgress$(): Subject<string> {
    return this.commandProgress;
  }

  getCommandError$(): Subject<string> {
    return this.commandError;
  }

  getCommandWarning$(): Subject<string> {
    return this.commandWarning;
  }

  fireCommandProgress(value: string) {
    this.commandProgress.next(value);
  }

  fireCommandError(value: string) {
    this.commandError.next(value);
  }

  fireCommandWarning(value: string) {
    this.commandWarning.next(value);
  }

  private commandCompleted = new Subject<CommandSideEffects>();

  getCommandCompleted$(): Subject<CommandSideEffects> {
    return this.commandCompleted;
  }

  fireCommandCompleted(result: CommandSideEffects) {
    this.commandCompleted.next(result);
  }

  private dirty = false;

  isDirty(): boolean {
    return this.dirty;
  }

  setDirty(dirty: boolean) {
    this.dirty = dirty;
  }

  canDeactivateImpl(appContext: AppContext, cmdContext: CommandContext): Observable<boolean> | boolean {
    if (!cmdContext._quitting) {
      if (cmdContext._busy) {
        appContext.getMessageSink()('WARN', 'busy');
        return false;
      }
      if (this.dirty) {
        const subj = new Subject<boolean>();
        appContext.getMessageBox(true)('dirty', (r) => {
          subj.next(r);
          subj.complete();
        });
        return subj;
      }
    }
    return true;
  }

  private dead = new ReplaySubject<void>();

  getDead$(): Observable<void> {
    return this.dead;
  }

  setDead() {
    if (this.printFormKey !== null) {
      this.printForm$.complete();
    }
    this.commandProgress.complete();
    this.commandError.complete();
    this.commandWarning.complete();
    this.commandCompleted.complete();
    this.dead.next();
    this.dead.complete();
  }
}

export enum ApplyEventStrategy {
  ReplaceLastIfMultiple,
}

export abstract class CommandContext {
  _quitting: boolean;
  _busy: boolean;
  _results: CommandSideEffects;

  abstract markDatabaseAsModified(): void;
  abstract getRequestDraft(request: RequestRoot | null): RequestRoot | null;
  abstract clearRequestsDrafts(): void;
  abstract reportProgress(page: CommandPage, key: string, ...args: any[]): void;
  abstract reportError(page: CommandPage, key: string, ...args: any[]): void;
  abstract reportWarning(page: CommandPage, key: string, ...args: any[]): void;

  abstract runCustomOperation<T>(page: CommandPage, errKey: string, operation: () => Observable<T>): Observable<T>;
  abstract countRequests(page: CommandPage, filter?: Object): Observable<number>;
  abstract findRequests(page: CommandPage, filter?: Object): Observable<RequestRoot[]>;
  abstract loadRequest(page: CommandPage, request: RequestRoot): Observable<RequestRoot>;
  abstract wipeRequest(page: CommandPage, request: RequestRoot): Observable<RequestRoot>;
  abstract saveRequest(page: CommandPage, request: RequestRoot): Observable<RequestRoot>;
  abstract applyRequestTransformer(
    page: CommandPage,
    request: RequestRoot,
    transformer: (features: any, dateValue?: Date) => void,
    wizardContext?: WizardContext,
    date?: Observable<Date>,
  ): Observable<RequestRoot>;
  abstract applyRequestStatus(request: RequestRoot, status: string): Observable<RequestRoot>;
  abstract applyRequestEvent(
    page: CommandPage,
    request: RequestRoot,
    event: string,
    date: Observable<Date>,
    strategy?: ApplyEventStrategy,
  ): Observable<RequestRoot>;
  abstract removeRequestEvent(request: RequestRoot, event: string): Observable<RequestRoot>;
  abstract clearRequestDraft(request: RequestRoot, isNew: boolean): Observable<RequestRoot>;
  abstract undirtyRequest(page: CommandPage, request: RequestRoot): Observable<RequestRoot>;
  abstract validateRequest(
    page: CommandPage,
    request: RequestRoot,
    date: Observable<Date>,
    continueCommandOnProblem: boolean,
    needSnackBar?: boolean,
  ): Observable<{ request: RequestRoot; good: boolean }>;
  abstract validateRequestQuiet(
    page: CommandPage,
    request: RequestRoot,
    date: Observable<Date>,
  ): Observable<{ request: RequestRoot; good: boolean }>;
  abstract confirmRequestsAction(page: CommandPage, requests: RequestRoot[], key: string, confirm: boolean): Observable<RequestRoot>;
  abstract openRequestPrintFragment(page: CommandPage, request: RequestRoot, key: string): Observable<RequestRoot>;
  abstract openRequestDataWizard(request: RequestRoot): Observable<RequestRoot>;
  abstract openRequestPassportVerifyWizard(request: RequestRoot | null): Observable<RequestRoot>;
  abstract closeRequestWizard(page: CommandPage, confirm: boolean): Observable<RequestRoot>;

  abstract getGrantedPassports(): Observable<PassportNumber[]>;
  abstract removeGrantedPassport(number: PassportNumber): Observable<PassportNumber>;
  abstract saveGrantedPassport(number: PassportNumber): Observable<PassportNumber>;
}

@Injectable()
export class CommandContextImpl implements CommandContext {
  _quitting: boolean = false;
  _busy: boolean = false;
  _results: CommandSideEffects;

  constructor(private appContext: AppContext, private reqService: RequestDataAccessService, private passportsStore: PassportsStore) {}

  private cachedDate(page: CommandPage, date: Observable<Date>): Observable<Date> {
    return defer(() => {
      if (!this._results.timestamp) {
        this._results.timestamp = new ReplaySubject<Date>(1);
        date.pipe(takeUntil(page.getCommandCompleted$()), takeUntil(page.getDead$())).subscribe(
          (dateValue) => {
            this._results.timestamp.next(dateValue);
            this._results.timestamp.complete();
          },
          (error) => {
            this._results.timestamp.error(error);
          },
        );
      }
      return this._results.timestamp;
    });
  }

  markDatabaseAsModified(): void {
    this._results.databaseModified = true;
  }

  getRequestDraft(request: RequestRoot | null): RequestRoot | null {
    return this.appContext.getDraft(request);
  }

  clearRequestsDrafts() {
    this.appContext.clearDrafts();
  }

  reportProgress(page: CommandPage, key: string, ...args: any[]) {
    page.fireCommandProgress(uFormat(this.appContext.getI18NString(key), ...args));
  }

  reportError(page: CommandPage, key: string, ...args: any[]) {
    page.fireCommandError(uFormat(this.appContext.getI18NString(key), ...args));
  }

  reportWarning(page: CommandPage, key: string, ...args: any[]) {
    page.fireCommandWarning(uFormat(this.appContext.getI18NString(key), ...args));
  }

  runCustomOperation<T>(page: CommandPage, errKey: string, operation: () => Observable<T>): Observable<T> {
    return new Observable((observer) => {
      page.incrementLoadingRef();
      const subscription = operation().subscribe(
        (r) => {
          observer.next(r);
        },
        (e) => {
          this.appContext.getMessageSink()('ERROR', errKey);
          observer.error(e);
        },
        () => {
          observer.complete();
        },
      );
      return () => {
        subscription.unsubscribe();
        page.decrementLoadingRef();
      };
    });
  }

  countRequests(page: CommandPage, filter?: Object): Observable<number> {
    const operation = () => this.reqService.count(filter || {});
    return this.runCustomOperation(page, 'fail.request.load', operation);
  }

  findRequests(page: CommandPage, filter?: Object): Observable<RequestRoot[]> {
    const operation = () => this.reqService.find(filter || {});
    return this.runCustomOperation(page, 'fail.request.load', operation);
  }

  loadRequest(page: CommandPage, request: RequestRoot): Observable<RequestRoot> {
    const operation = () => this.reqService.load(request);
    return this.runCustomOperation(page, 'fail.request.load', operation);
  }

  wipeRequest(page: CommandPage, request: RequestRoot): Observable<RequestRoot> {
    const operation = () => {
      this._results.databaseModified = true;
      return this.reqService.wipe(request).pipe(map((x) => request));
    };
    return this.runCustomOperation(page, 'fail.request.delete', operation);
  }

  saveRequest(page: CommandPage, request: RequestRoot): Observable<RequestRoot> {
    const operation = () => {
      this._results.databaseModified = true;
      return (request.id ? this.reqService.edit(request) : this.reqService.save(request)).pipe(map((x) => request));
    };
    return this.runCustomOperation(page, 'fail.request.save', operation);
  }

  applyRequestTransformer(
    page: CommandPage,
    request: RequestRoot,
    transformer: (features: any, dateValue?: Date) => void,
    wizardContext?: WizardContext,
    date?: Observable<Date>,
  ): Observable<RequestRoot> {
    const operation = (dateValue?: Date) =>
      new Observable<RequestRoot>((observer) => {
        transformer(request.features, dateValue);
        Features.resetEnumFields(request.features, new request.features.constructor());
        Features.updateFieldsVisibility(request.features, new request.features.constructor());
        if (wizardContext) {
          wizardContext.notifyModelListeners();
        }
        observer.next(request);
        observer.complete();
      });
    const date_ = date ? this.cachedDate(page, date) : of<Date | undefined>(undefined);
    return date_.pipe(concatMap((dateValue) => operation(dateValue)));
  }

  applyRequestStatus(request: RequestRoot, status: string): Observable<RequestRoot> {
    return new Observable((observer) => {
      request.status = status;
      observer.next(request);
      observer.complete();
    });
  }

  applyRequestEvent(
    page: CommandPage,
    request: RequestRoot,
    event: string,
    date: Observable<Date>,
    strategy?: ApplyEventStrategy,
  ): Observable<RequestRoot> {
    const e = new RequestEvent();
    e.type = event;

    const comment$ = of('').pipe(tap((x) => (e.comment = x)));
    const date$ = this.cachedDate(page, date).pipe(tap((x) => (e.created = e.processed = x)));

    return concat(comment$, date$).pipe(
      toArray(),
      concatMap((x) => {
        if (request.eventHistory) {
          if (strategy === ApplyEventStrategy.ReplaceLastIfMultiple) {
            const events = request.eventHistory.filter((x) => x.type === event);
            if (events.length > 1) {
              request.eventHistory[request.eventHistory.findIndex((x) => x === events[events.length - 1])] = e;
            } else {
              request.eventHistory.push(e);
            }
          } else {
            request.eventHistory.push(e);
          }
        }
        return of(request);
      }),
    );
  }

  removeRequestEvent(request: RequestRoot, event: string): Observable<RequestRoot> {
    return new Observable((observer) => {
      if (request.eventHistory) {
        request.eventHistory = request.eventHistory.filter((e) => e.type !== event);
      }
      observer.next(request);
      observer.complete();
    });
  }

  clearRequestDraft(request: RequestRoot, isNew: boolean): Observable<RequestRoot> {
    return new Observable((observer) => {
      this.appContext.clearDraft(isNew ? null : request);
      observer.next(request);
      observer.complete();
    });
  }

  undirtyRequest(page: CommandPage, request: RequestRoot): Observable<RequestRoot> {
    return new Observable((observer) => {
      page.setDirty(false);
      observer.next(request);
      observer.complete();
    });
  }

  validateRequest(
    page: CommandPage,
    request: RequestRoot,
    date: Observable<Date>,
    continueCommandOnProblem: boolean,
    needSnackBar: boolean = true,
  ): Observable<{ request: RequestRoot; good: boolean }> {
    const operation = (dateValue: Date) =>
      new Observable<{ request: RequestRoot; good: boolean }>((observer) => {
        const validationBuilder = new ValidationBuilder(this.appContext, request.features);
        const validationResult = validationBuilder.validate(request.features, UTCDate.today(dateValue));
        if (validationResult.hasERROR()) {
          if (needSnackBar) this.appContext.getMessageSink()('ERROR', 'fail.request.validate!');
          this._results.validationTriggered = validationResult;
          if (continueCommandOnProblem) {
            observer.next({ request: request, good: false });
          }
          observer.complete();
        } else if (validationResult.hasWARNING()) {
          const messages = validationResult
            .getErrors()
            .filter((x) => x.severity === ValidationSeverity.WARNING)
            .map((x) => x.message);
          this.appContext.getValidationWarningMessageBox()(messages, (x) => {
            if (x) {
              observer.next({ request: request, good: true });
            } else {
              this._results.validationTriggered = validationResult;
              if (continueCommandOnProblem) {
                observer.next({ request: request, good: false });
              }
            }
            observer.complete();
          });
        } else {
          observer.next({ request: request, good: true });
          observer.complete();
        }
      });
    return this.cachedDate(page, date).pipe(concatMap((dateValue) => operation(dateValue)));
  }

  validateRequestQuiet(
    page: CommandPage,
    request: RequestRoot,
    date: Observable<Date>,
  ): Observable<{ request: RequestRoot; good: boolean }> {
    const operation = (dateValue: Date) =>
      new Observable<{ request: RequestRoot; good: boolean }>((observer) => {
        const validationBuilder = new ValidationBuilder(this.appContext, request.features);
        const validationResult = validationBuilder.validate(request.features, UTCDate.today(dateValue));
        observer.next({ request: request, good: !validationResult.hasERROR() && !validationResult.hasWARNING() });
        observer.complete();
      });
    return this.cachedDate(page, date).pipe(concatMap((dateValue) => operation(dateValue)));
  }

  confirmRequestsAction(page: CommandPage, requests: RequestRoot[], key: string, confirm: boolean): Observable<RequestRoot> {
    return new Observable((observer) => {
      this.appContext.getMessageBox(confirm)(key, (x) => {
        if (x) {
          for (const request of requests) {
            observer.next(request);
          }
        }
        observer.complete();
      });
    });
  }

  openRequestPrintFragment(page: CommandPage, request: RequestRoot, key: string): Observable<RequestRoot> {
    return new Observable((observer) => {
      this.appContext.changeMessageSinkVisibleRef(false);
      this.appContext.changeMainMenuVisibleRef(false);
      const subscription = page.openPrintForm(key, request).subscribe(
        (r) => {
          observer.next(r);
        },
        (e) => {
          observer.error(e);
        },
        () => {
          observer.complete();
        },
      );
      return () => {
        subscription.unsubscribe();
        this.appContext.changeMessageSinkVisibleRef(true);
        this.appContext.changeMainMenuVisibleRef(true);
      };
    });
  }

  openRequestDataWizard(request: RequestRoot): Observable<RequestRoot> {
    return new Observable((observer) => {
      this.appContext.openDataWizard(request);
      observer.complete();
    });
  }

  openRequestPassportVerifyWizard(request: RequestRoot | null): Observable<RequestRoot> {
    return new Observable((observer) => {
      this.appContext.openPassportVerifyWizard(request);
      observer.complete();
    });
  }

  closeRequestWizard(page: CommandPage, confirm: boolean): Observable<RequestRoot> {
    return new Observable((observer) => {
      if (!confirm) {
        page.setDirty(false);
      }
      this.appContext.closeWizard();
      observer.complete();
    });
  }

  getGrantedPassports(): Observable<PassportNumber[]> {
    return this.passportsStore.getGrantedPassports().pipe(toArray());
  }

  removeGrantedPassport(number: PassportNumber): Observable<PassportNumber> {
    return this.passportsStore
      .removeGrantedPassport(number)
      .pipe(concatMap((x) => (x ? of(number) : throwError('Cannot remove passport'))));
  }

  saveGrantedPassport(number: PassportNumber): Observable<PassportNumber> {
    return this.passportsStore.saveGrantedPassport(number).pipe(concatMap((x) => (x ? of(number) : throwError('Cannot save passport'))));
  }
}

export interface Command<T> {
  icon(): string;
  caption(): string;
  color(): boolean;
  visible(ctx: CommandContext, selection: T[], all: T[]): boolean;
  enabled(ctx: CommandContext, selection: T[]): boolean;
  execute(ctx: CommandContext, page: CommandPage, objects: T[]): Observable<T>;
}

export class CommandData<T> {
  command: Command<T>;
  onBefore: () => void;
}

export class CommandWrapper<T> {
  private command: Command<T>;
  private onBefore: () => void;

  constructor(
    private appContext: AppContext,
    private cmdContext: CommandContext,
    private page: CommandPage,
    commandData: Command<T> | CommandData<T>,
  ) {
    this.command = (commandData as any).command ? (commandData as CommandData<T>).command : (commandData as Command<T>);
    this.onBefore = (commandData as any).command ? (commandData as CommandData<T>).onBefore : () => {};
  }

  icon(): string {
    return this.command.icon();
  }

  caption(): string {
    return this.appContext.getI18NString(this.command.caption());
  }

  color(): boolean {
    return this.command.color();
  }

  visible(selection: SelectionModel<TableItem<T>> | T[], items: TableItem<T>[]): boolean {
    const selected = selection instanceof SelectionModel ? selection.selected.map((x) => x.item) : selection;
    return this.command.visible(
      this.cmdContext,
      selected,
      items.map((x) => x.item),
    );
  }

  disabled(selection: SelectionModel<TableItem<T>> | T[]): boolean {
    const selected = selection instanceof SelectionModel ? selection.selected.map((x) => x.item) : selection;
    return !this.command.enabled(this.cmdContext, selected);
  }

  execute(selection: SelectionModel<TableItem<T>> | T[], cancel?: Observable<any>) {
    if (this.onBefore) {
      this.onBefore();
    }
    if (this.cmdContext._busy) {
      this.appContext.getMessageSink()('WARN', 'busy');
      return;
    }
    this.cmdContext._busy = true;
    this.page.incrementLoadingRef();
    this.cmdContext._results = new CommandSideEffects();
    const selected = selection instanceof SelectionModel ? selection.selected.map((x) => x.item) : selection;
    this.command
      .execute(this.cmdContext, this.page, selected)
      .pipe(takeUntil(this.page.getDead$()), takeUntil(cancel ? cancel : new Subject<void>()))
      .subscribe(
        (r) => {},
        (e) => {
          console.log(e);
          this.cmdContext._busy = false;
          this.page.decrementLoadingRef();
          this.page.fireCommandCompleted(this.cmdContext._results);
        },
        () => {
          this.cmdContext._busy = false;
          this.page.decrementLoadingRef();
          this.page.fireCommandCompleted(this.cmdContext._results);
        },
      );
  }
}

export class CommandWrapperList<T> {
  private list: CommandWrapper<T>[];

  constructor(page: CommandPage, appContext: AppContext, cmdContext: CommandContext, list: (Command<T> | CommandData<T>)[]) {
    this.list = list.map((x) => new CommandWrapper<T>(appContext, cmdContext, page, x));
  }

  get(selection: SelectionModel<TableItem<T>> | T[], items: TableItem<T>[]): CommandWrapper<T>[] {
    return this.list.filter((x) => x.visible(selection, items));
  }
}
